import { useCallback, useEffect, useMemo, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { SelectDropdown } from "../SelectDropdown";
import { DatePickerInput } from "../DatePickerInput";
import { LoaderSpinner } from "../LoaderSpinner";
import {
    blockUnsignedIntegerKeyDown,
    sanitizeUnsignedIntegerString,
} from "../../utils/integerInput";

const SKIP = "— skip —";

const boolSelectOptions = [
    { label: SKIP, value: "" },
    { label: "Yes", value: "true" },
    { label: "No", value: "false" },
];

const parseBool = (v) => {
    if (v === "true") return true;
    if (v === "false") return false;
    return undefined;
};

const FieldRow = ({ label, children }) => (
    <div className="grid grid-cols-2 items-center gap-3">
        <label className="shrink-0 text-xs font-bold text-gray-500 sm:w-40">{label}</label>
        <div className="min-w-0 flex-1">{children}</div>
    </div>
);

export const ProductCatalogBulkSectionModals = ({
    openSection,
    onClose,
    selectedCount,
    productIds,
    statusOptions = [],
    vendorSelectOptions = [],
    vendorSelectLoading = false,
    containerGroupOptions = [],
    onApply,
    disabled = false,
}) => {
    const [submitting, setSubmitting] = useState(false);

    const [identityStatus, setIdentityStatus] = useState("");
    const [identityPackaging, setIdentityPackaging] = useState("");
    const [identitySupplies, setIdentitySupplies] = useState("");
    const [attributesMaterial, setAttributesMaterial] = useState("");

    const [opsVendor, setOpsVendor] = useState("");
    const [opsLead, setOpsLead] = useState("");
    const [opsMoq, setOpsMoq] = useState("");
    const [opsContainerGroupId, setOpsContainerGroupId] = useState("");
    const [opsContainerQty, setOpsContainerQty] = useState("");
    const [opsCartonQty, setOpsCartonQty] = useState("");

    const [physWeight, setPhysWeight] = useState("");
    const [physLength, setPhysLength] = useState("");
    const [physWidth, setPhysWidth] = useState("");
    const [physHeight, setPhysHeight] = useState("");

    const [lifeLaunch, setLifeLaunch] = useState("");
    const [lifeSunset, setLifeSunset] = useState("");
    const [lifeInactive, setLifeInactive] = useState("");

    const [flgVirtual, setFlgVirtual] = useState("");
    const [flgForecast, setFlgForecast] = useState("");
    const [flgReview, setFlgReview] = useState("");
    const [flgLock, setFlgLock] = useState("");
    const [flgReplaceExit, setFlgReplaceExit] = useState("");

    const vendorDropdownOptions = useMemo(
        () => [
            { label: SKIP, value: "" },
            ...(vendorSelectOptions || []).map((o) => ({
                label: o.label,
                value: String(o.value),
            })),
        ],
        [vendorSelectOptions]
    );
    const identityStatusOptions = useMemo(
        () => [{ label: SKIP, value: "" }, ...(statusOptions || [])],
        [statusOptions]
    );

    const resetForms = useCallback(() => {
        setIdentityStatus("");
        setIdentityPackaging("");
        setIdentitySupplies("");
        setAttributesMaterial("");
        setOpsVendor("");
        setOpsLead("");
        setOpsMoq("");
        setOpsContainerGroupId("");
        setOpsContainerQty("");
        setOpsCartonQty("");
        setPhysWeight("");
        setPhysLength("");
        setPhysWidth("");
        setPhysHeight("");
        setLifeLaunch("");
        setLifeSunset("");
        setLifeInactive("");
        setFlgVirtual("");
        setFlgForecast("");
        setFlgReview("");
        setFlgLock("");
        setFlgReplaceExit("");
    }, []);

    useEffect(() => {
        if (openSection) resetForms();
    }, [openSection, resetForms]);

    const subtitle = useMemo(
        () =>
            `Only filled fields will be applied. Empty fields are skipped. Affects <span class="font-bold">${selectedCount}</span> selected product${selectedCount === 1 ? "" : "s"}.`,
        [selectedCount]
    );

    const handleSubmit = async () => {
        if (!onApply || !productIds?.length) return;
        setSubmitting(true);
        try {
            if (openSection === "identity") {
                const settings = {};
                if (identityStatus) settings.status = identityStatus;
                const packaging = parseBool(identityPackaging);
                if (packaging !== undefined) settings.is_packaging = packaging;
                const supplies = parseBool(identitySupplies);
                if (supplies !== undefined) settings.is_supply = supplies;
                if (Object.keys(settings).length === 0) {
                    return;
                }
                await onApply({ productIds, settings });
            } else if (openSection === "attributes") {
                const material = attributesMaterial.trim();
                if (!material) {
                    return;
                }
                await onApply({ productIds, settings: { material } });
            } else if (openSection === "operations") {
                const settings = {};
                if (opsLead.trim()) {
                    const n = parseInt(sanitizeUnsignedIntegerString(opsLead), 10);
                    if (Number.isFinite(n)) settings.lead_time_days = String(n);
                }
                if (opsMoq.trim()) {
                    const n = parseInt(sanitizeUnsignedIntegerString(opsMoq), 10);
                    if (Number.isFinite(n)) settings.moq = String(n);
                }
                if (opsContainerGroupId.trim()) settings.container_group_id = opsContainerGroupId.trim();
                if (opsContainerQty.trim()) {
                    const n = parseInt(sanitizeUnsignedIntegerString(opsContainerQty), 10);
                    if (Number.isFinite(n)) settings.container_qty = String(n);
                }
                if (opsCartonQty.trim()) {
                    const n = parseInt(sanitizeUnsignedIntegerString(opsCartonQty), 10);
                    if (Number.isFinite(n)) settings.carton_qty = String(n);
                }
                const vendorId = opsVendor !== undefined && opsVendor !== null ? String(opsVendor) : "";
                const hasVendor = vendorId.trim() !== "";
                const hasSettings = Object.keys(settings).length > 0;
                if (!hasVendor && !hasSettings) {
                    return;
                }
                await onApply({
                    productIds,
                    vendorId: hasVendor ? vendorId : undefined,
                    settings: hasSettings ? settings : undefined,
                });
            } else if (openSection === "physical") {
                const settings = {};
                const trimDec = (s) => String(s || "").trim();
                if (trimDec(physWeight)) settings.unit_weight_lbs = trimDec(physWeight);
                if (trimDec(physLength)) settings.unit_length = trimDec(physLength);
                if (trimDec(physWidth)) settings.unit_width = trimDec(physWidth);
                if (trimDec(physHeight)) settings.unit_height = trimDec(physHeight);
                if (Object.keys(settings).length === 0) {
                    return;
                }
                await onApply({ productIds, settings });
            } else if (openSection === "lifecycle") {
                const settings = {};
                if (lifeLaunch && /^\d{4}-\d{2}-\d{2}$/.test(lifeLaunch)) settings.launch_date = lifeLaunch;
                if (lifeSunset && /^\d{4}-\d{2}-\d{2}$/.test(lifeSunset)) settings.sunset_date = lifeSunset;
                if (lifeInactive && /^\d{4}-\d{2}-\d{2}$/.test(lifeInactive)) settings.inactive_date = lifeInactive;
                if (Object.keys(settings).length === 0) {
                    return;
                }
                await onApply({ productIds, settings });
            } else if (openSection === "flags") {
                const settings = {};
                const v = parseBool(flgVirtual);
                if (v !== undefined) settings.is_virtual = v;
                const f = parseBool(flgForecast);
                if (f !== undefined) settings.forecastable = f;
                const r = parseBool(flgReview);
                if (r !== undefined) settings.needs_review = r;
                const l = parseBool(flgLock);
                if (l !== undefined) settings.lock_flag = l;
                const x = parseBool(flgReplaceExit);
                if (x !== undefined) settings.replacement_exit = x;
                if (Object.keys(settings).length === 0) {
                    return;
                }
                await onApply({ productIds, settings });
            }
            onClose();
            resetForms();
        } finally {
            setSubmitting(false);
        }
    };

    if (!openSection) return null;

    const titles = {
        identity: "Bulk Edit — Identity",
        attributes: "Bulk Edit — Attributes",
        operations: "Bulk Edit — Operations",
        physical: "Bulk Edit — Physical",
        lifecycle: "Bulk Edit — Lifecycle",
        flags: "Bulk Edit — Flags",
    };

    const busy = submitting || disabled;

    return (
        <div
            className="fixed inset-0 z-[80] flex items-center justify-center p-4"
            aria-modal="true"
            role="dialog"
        >
            <button
                type="button"
                className="absolute inset-0 bg-gray-900/40"
                aria-label="Close"
                onClick={onClose}
            />
            <div className="relative z-[81] flex max-h-[90vh] w-full max-w-[512px] flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl">
                <div className="flex items-start justify-between gap-3 p-6 pb-3">
                    <div>
                        <h2 className="text-base font-semibold text-gray-900">{titles[openSection]}</h2>
                        <p className="mt-2 text-xs text-gray-500" dangerouslySetInnerHTML={{ __html: subtitle }} />
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                        aria-label="Close"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>

                <div className="overflow-y-auto p-6 pb-3 pt-3">
                    {openSection === "identity" && (
                        <div className="flex flex-col gap-3">
                            <FieldRow label="Status">
                                <SelectDropdown
                                    portal
                                    options={identityStatusOptions}
                                    value={identityStatus}
                                    placeholder={SKIP}
                                    disabled={busy}
                                    selectClassname="!py-1.5 !bg-white"
                                    onChange={(v) => setIdentityStatus(v ?? "")}
                                />
                            </FieldRow>
                            <FieldRow label="Packaging">
                                <SelectDropdown
                                    portal
                                    options={boolSelectOptions}
                                    value={identityPackaging}
                                    placeholder={SKIP}
                                    disabled={busy}
                                    selectClassname="!py-1.5 !bg-white"
                                    onChange={(v) => setIdentityPackaging(v ?? "")}
                                />
                            </FieldRow>
                            <FieldRow label="Supplies">
                                <SelectDropdown
                                    portal
                                    options={boolSelectOptions}
                                    value={identitySupplies}
                                    placeholder={SKIP}
                                    disabled={busy}
                                    selectClassname="!py-1.5 !bg-white"
                                    onChange={(v) => setIdentitySupplies(v ?? "")}
                                />
                            </FieldRow>
                        </div>
                    )}

                    {openSection === "attributes" && (
                        <div className="flex flex-col gap-2">
                            <FieldRow label="Material">
                                <textarea
                                    value={attributesMaterial}
                                    onChange={(e) => setAttributesMaterial(e.target.value)}
                                    disabled={busy}
                                    placeholder={SKIP}
                                    rows={4}
                                    className="w-full resize-y rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-primary"
                                />
                            </FieldRow>
                            <p className="text-xs text-gray-500">
                                Replaces the existing material on every selected product.
                            </p>
                        </div>
                    )}

                    {openSection === "operations" && (
                        <div className="flex flex-col gap-3">
                            <FieldRow label="Supplier">
                                <SelectDropdown
                                    portal
                                    options={vendorDropdownOptions}
                                    value={opsVendor}
                                    placeholder={SKIP}
                                    disabled={vendorSelectLoading || busy}
                                    selectClassname={`!py-1.5 !bg-white`}
                                    selectedValueClassname={`${opsVendor ? "" : "!text-gray-500"}`}
                                    onChange={(v) => setOpsVendor(v ?? "")}
                                />
                            </FieldRow>
                            <FieldRow label="Lead Time (days)">
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="off"
                                    placeholder={SKIP}
                                    value={opsLead}
                                    onKeyDown={blockUnsignedIntegerKeyDown}
                                    onChange={(e) =>
                                        setOpsLead(sanitizeUnsignedIntegerString(e.target.value))
                                    }
                                    disabled={busy}
                                    className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-primary"
                                />
                            </FieldRow>
                            <FieldRow label="MOQ">
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="off"
                                    placeholder={SKIP}
                                    value={opsMoq}
                                    onKeyDown={blockUnsignedIntegerKeyDown}
                                    onChange={(e) =>
                                        setOpsMoq(sanitizeUnsignedIntegerString(e.target.value))
                                    }
                                    disabled={busy}
                                    className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-primary"
                                />
                            </FieldRow>
                            <FieldRow label="Container Group">
                                <SelectDropdown
                                    options={[{ label: SKIP, value: "" }, ...(containerGroupOptions || [])]}
                                    value={opsContainerGroupId}
                                    onChange={setOpsContainerGroupId}
                                    disabled={busy}
                                    noneLabel={SKIP}
                                    allLabel="All container groups"
                                    className="w-full"
                                />
                            </FieldRow>
                            <FieldRow label="Container QTY">
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="off"
                                    placeholder={SKIP}
                                    value={opsContainerQty}
                                    onKeyDown={blockUnsignedIntegerKeyDown}
                                    onChange={(e) =>
                                        setOpsContainerQty(sanitizeUnsignedIntegerString(e.target.value))
                                    }
                                    disabled={busy}
                                    className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-primary"
                                />
                            </FieldRow>
                            <FieldRow label="Carton QTY">
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="off"
                                    placeholder={SKIP}
                                    value={opsCartonQty}
                                    onKeyDown={blockUnsignedIntegerKeyDown}
                                    onChange={(e) =>
                                        setOpsCartonQty(sanitizeUnsignedIntegerString(e.target.value))
                                    }
                                    disabled={busy}
                                    className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-primary"
                                />
                            </FieldRow>
                        </div>
                    )}

                    {openSection === "physical" && (
                        <div className="flex flex-col gap-3">
                            <FieldRow label="Weight (lbs)">
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    autoComplete="off"
                                    placeholder={SKIP}
                                    value={physWeight}
                                    onChange={(e) => setPhysWeight(e.target.value)}
                                    disabled={busy}
                                    className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-primary"
                                />
                            </FieldRow>
                            <FieldRow label="Length (in)">
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    autoComplete="off"
                                    placeholder={SKIP}
                                    value={physLength}
                                    onChange={(e) => setPhysLength(e.target.value)}
                                    disabled={busy}
                                    className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-primary"
                                />
                            </FieldRow>
                            <FieldRow label="Width (in)">
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    autoComplete="off"
                                    placeholder={SKIP}
                                    value={physWidth}
                                    onChange={(e) => setPhysWidth(e.target.value)}
                                    disabled={busy}
                                    className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-primary"
                                />
                            </FieldRow>
                            <FieldRow label="Height (in)">
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    autoComplete="off"
                                    placeholder={SKIP}
                                    value={physHeight}
                                    onChange={(e) => setPhysHeight(e.target.value)}
                                    disabled={busy}
                                    className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-primary"
                                />
                            </FieldRow>
                        </div>
                    )}

                    {openSection === "lifecycle" && (
                        <div className="flex flex-col gap-3">
                            <FieldRow label="Launch Date">
                                <DatePickerInput
                                    value={lifeLaunch}
                                    onChange={(iso) => setLifeLaunch(iso || "")}
                                    placeholder="Pick a date"
                                    className="w-full"
                                    buttonClassName="!py-2 !px-3 !text-sm"
                                    disabled={busy}
                                />
                            </FieldRow>
                            <FieldRow label="Sunset Date">
                                <DatePickerInput
                                    value={lifeSunset}
                                    onChange={(iso) => setLifeSunset(iso || "")}
                                    placeholder="Pick a date"
                                    className="w-full"
                                    buttonClassName="!py-2 !px-3 !text-sm"
                                    disabled={busy}
                                />
                            </FieldRow>
                            <FieldRow label="Inactive Date">
                                <DatePickerInput
                                    value={lifeInactive}
                                    onChange={(iso) => setLifeInactive(iso || "")}
                                    placeholder="Pick a date"
                                    className="w-full"
                                    buttonClassName="!py-2 !px-3 !text-sm"
                                    disabled={busy}
                                />
                            </FieldRow>
                        </div>
                    )}

                    {openSection === "flags" && (
                        <div className="flex flex-col gap-3">
                            <FieldRow label="Virtual Product">
                                <SelectDropdown
                                    portal
                                    options={boolSelectOptions}
                                    value={flgVirtual}
                                    placeholder={SKIP}
                                    disabled={busy}
                                    selectClassname="!py-1.5 !bg-white"
                                    onChange={(v) => setFlgVirtual(v ?? "")}
                                />
                            </FieldRow>
                            <FieldRow label="Forecastable">
                                <SelectDropdown
                                    portal
                                    options={boolSelectOptions}
                                    value={flgForecast}
                                    placeholder={SKIP}
                                    disabled={busy}
                                    selectClassname="!py-1.5 !bg-white"
                                    onChange={(v) => setFlgForecast(v ?? "")}
                                />
                            </FieldRow>
                            <FieldRow label="Needs Review">
                                <SelectDropdown
                                    portal
                                    options={boolSelectOptions}
                                    value={flgReview}
                                    placeholder={SKIP}
                                    disabled={busy}
                                    selectClassname="!py-1.5 !bg-white"
                                    onChange={(v) => setFlgReview(v ?? "")}
                                />
                            </FieldRow>
                            <FieldRow label="Lock Flag">
                                <SelectDropdown
                                    portal
                                    options={boolSelectOptions}
                                    value={flgLock}
                                    placeholder={SKIP}
                                    disabled={busy}
                                    selectClassname="!py-1.5 !bg-white"
                                    onChange={(v) => setFlgLock(v ?? "")}
                                />
                            </FieldRow>
                            <FieldRow label="Replacement Exit">
                                <SelectDropdown
                                    portal
                                    options={boolSelectOptions}
                                    value={flgReplaceExit}
                                    placeholder={SKIP}
                                    disabled={busy}
                                    selectClassname="!py-1.5 !bg-white"
                                    onChange={(v) => setFlgReplaceExit(v ?? "")}
                                />
                            </FieldRow>
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-2 p-6 pt-3">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={busy}
                        className="inline-flex min-w-[5rem] items-center justify-center rounded-md border border-gray-300 bg-white px-2 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={() => void handleSubmit()}
                        disabled={busy}
                        className="inline-flex min-w-[10rem] items-center justify-center gap-2 rounded-md border border-transparent bg-black px-2 py-1 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
                    >
                        {busy ? (
                            <LoaderSpinner width="18" height="18" border="2" colorSpinner="white" />
                        ) : (
                            `Apply to ${selectedCount} product${selectedCount === 1 ? "" : "s"}`
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};
